import { groupBy } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import addProductVariants from '@/shared/api/shipping/productVariants/addProductVariants';
import removeVariants from '@/shared/api/shipping/productVariants/removeVariants';
import updateShippingProfile from '@/shared/api/shipping/profiles/updateShippingProfile';
import createMultipleZones from '@/shared/api/shipping/zones/createMultipleZones';
import removeZones from '@/shared/api/shipping/zones/removeZones';
import updateZones from '@/shared/api/shipping/zones/updateZones';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import ProductVariant from '@/shared/types/products/productVariant.type';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';
import Zone from '@/shared/types/shipping/zones/zone.type';

import getVariantIdsByProductId from '../../utils/getVariantsIdsByProductId';
import { DialogDataType } from '../EditShippingProfile';

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<DialogDataType<DialogName>, DialogName>
>['onUpdateDialogState'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  profileId: string;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const useEditShippingProfileOperations = ({
  profileId,
  onUpdateNotificationBar,
  onUpdateDialogState,
  onUpdateProgressDialog,
}: Args) => {
  const { t } = useTranslation(['shipping', 'common']);

  const redirectLogin = useRedirectLogin();

  const [isShippingProfileUpdating, setIsShippingProfileUpdating] =
    useState(false);

  const [updatedShippingProfile, setUpdatedShippingProfile] = useState<
    ShippingProfile[] | null
  >(null);

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const handleUpdateProfile = async (value: Partial<ShippingProfile>) => {
    setIsShippingProfileUpdating(true);

    const response = await updateShippingProfile({
      id: profileId,
      body: value,
      onUnauthorized: redirectLogin,
    });

    setIsShippingProfileUpdating(false);

    if (response.success) {
      onUpdateNotificationBar({
        message: t('shipping:shippingProfileDetailsSuccessMessage'),
        open: true,
      });
    } else {
      onUpdateDialogState(DialogName.Error, {
        open: true,
        errors: [response.msg],
        data: {
          title: t('shipping:shippingProfileDetails'),
          message: t('shipping:shippingProfileDetailsErrorMessage'),
        },
      });
    }
  };

  const handleAssignVariants = async (values: ProductVariant[]) => {
    const groups = groupBy(values, (item) => item.product_id);

    const requestBody = Object.entries(groups).map(([key, value]) => ({
      product_id: key,
      variants: value.flatMap((item) => item.variant_id),
    }));

    onUpdateProgressDialog({
      open: true,
      title: 'Assigning products and variants',
      progress: 0,
    });

    const response = await addProductVariants({
      profileId,
      body: { product_variants: requestBody },
      onUnauthorized: redirectLogin,
    });

    onUpdateProgressDialog({ open: false });

    if (response.success) {
      onUpdateDialogState(DialogName.AssignProducts, { open: false });
      setUpdatedShippingProfile(response.data);
      onUpdateNotificationBar({
        message: t('shipping:variantsAssignedSuccess'),
        open: true,
      });
    } else {
      onUpdateDialogState(DialogName.AssignProducts, {
        errors: [response.msg],
      });
    }
  };

  const handleRemoveVariants = async (values: ProductVariant[]) => {
    onUpdateProgressDialog({
      open: true,
      title: t('shipping:removingVariants'),
      progress: 0,
    });

    const isProduct = values.findIndex((item) => 'variant_name' in item) === -1;

    const requestBody = isProduct
      ? values.map((item) => ({
          product_id: item.product_id,
          variant_id: [],
        }))
      : getVariantIdsByProductId(values).map((item) => ({
          product_id: item.product_id,
          variant_id: item.variants,
        }));

    const response = await removeVariants({
      profileId,
      body: requestBody,
      onProgress: (progress) => onUpdateProgressDialog({ progress }),
      onUnauthorized: redirectLogin,
    });

    onUpdateProgressDialog({ open: false });

    if (response.every((res) => res.success)) {
      onUpdateDialogState(DialogName.ConfirmationRemoveAssignedVariants, {
        open: false,
      });
      setUpdatedShippingProfile(response[response.length - 1].data);
      onUpdateNotificationBar({
        message: t('shipping:variantsRemovedSuccess'),
        open: true,
      });
    } else {
      onUpdateDialogState(DialogName.RemoveContacts, {
        loading: false,
        errors: response.filter((res) => !res.success).map((res) => res.msg),
      });
    }
  };

  const handleAddZone = async (
    value: Omit<Zone, 'delivery_state'> & {
      delivery_state: string[];
    },
  ) => {
    onUpdateProgressDialog({
      open: true,
      title: t('shipping:addingNewZone', {
        count: value.delivery_state.length,
      }),
      progress: 0,
    });

    const response = await createMultipleZones({
      profileId,
      body: value,
      onUnauthorized: redirectLogin,
      onProgress: (progress) => onUpdateProgressDialog({ progress }),
    });

    onUpdateProgressDialog({ open: false });

    if (response.every((res) => res.success)) {
      setUpdatedShippingProfile(response[response.length - 1].data);
      onUpdateDialogState(DialogName.AddZone, {
        open: false,
      });
      onUpdateNotificationBar({
        message: t('shipping:zoneAddedSuccess', { count: response.length }),
        open: true,
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateDialogState(DialogName.AddZone, {
        loading: false,
        errors: response.filter((res) => !res.success).map((res) => res.msg),
      });
    }
  };

  const handleUpdateZoneRate = async (
    zones: Zone[],
    valueToUpdate: Pick<Zone, 'shipping_rate' | 'shipping_rate_type'>,
  ) => {
    onUpdateProgressDialog({
      open: true,
      title: t('shipping:updatingZoneRate'),
      progress: 0,
    });

    const response = await updateZones({
      profileId,
      zoneIds: zones.map((item) => item.shipping_profile_zone_id),
      body: valueToUpdate,
      onUnauthorized: redirectLogin,
      onProgress: (progress) => onUpdateProgressDialog({ progress }),
    });

    onUpdateProgressDialog({ open: false });

    if (response.every((res) => res.success)) {
      setUpdatedShippingProfile(response[response.length - 1].data);
      onUpdateDialogState(DialogName.AddZone, {
        open: false,
      });
      onUpdateNotificationBar({
        message: t('shipping:shippingRateUpdated', { count: response.length }),
        open: true,
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateDialogState(DialogName.AddZone, {
        loading: false,
        errors: response.filter((res) => !res.success).map((res) => res.msg),
      });
    }
  };

  const handleRemoveZones = async (zones: Zone[]) => {
    onUpdateProgressDialog({
      open: true,
      title: t('shipping:removingZones'),
      progress: 0,
    });

    const response = await removeZones({
      profileId,
      zoneIds: zones.map((item) => item.shipping_profile_zone_id),
      onUnauthorized: redirectLogin,
      onProgress: (progress) => onUpdateProgressDialog({ progress }),
    });

    onUpdateProgressDialog({ open: false });

    if (response.every((res) => res.success)) {
      setUpdatedShippingProfile(response[response.length - 1].data);
      onUpdateDialogState(DialogName.ConfirmationRemoveZones, {
        open: false,
      });
      onUpdateNotificationBar({
        message: t('shipping:zoneRemovedSuccess', { count: response.length }),
        open: true,
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateDialogState(DialogName.ConfirmationRemoveZones, {
        loading: false,
        errors: response.filter((res) => !res.success).map((res) => res.msg),
      });
    }
  };

  return {
    refreshTableIndex,
    isShippingProfileUpdating,
    updatedShippingProfile,
    handleAssignVariants,
    handleUpdateProfile,
    handleRemoveVariants,
    handleAddZone,
    handleUpdateZoneRate,
    handleRemoveZones,
  };
};

export default useEditShippingProfileOperations;
